import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Users, Clock, BarChart3, TrendingUp } from 'lucide-react';
import { AnalyticsSummary } from '../types';

interface StatsGridProps {
  analytics: AnalyticsSummary | undefined;
  isLoading: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

export function StatsGrid({ analytics, isLoading }: StatsGridProps) {
  const totalScans = analytics?.total_scans || 0;
  const uniqueUsers = analytics?.unique_users || 0;
  const uniqueRate = totalScans > 0 ? ((uniqueUsers / totalScans) * 100).toFixed(1) : '0';

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-white rounded-xl p-4 border border-[--clinqr-border] animate-pulse">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 rounded-lg bg-gray-200"></div>
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="w-14 h-7 bg-gray-200 rounded mb-1"></div>
            <div className="w-16 h-3 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const stats = [
    {
      label: 'Total Scans',
      value: totalScans.toLocaleString(),
      icon: Eye,
      gradient: 'from-[--clinqr-navy] to-[--clinqr-slate-dark]',
      showTrend: true,
    },
    {
      label: 'Unique Users',
      value: uniqueUsers.toLocaleString(),
      icon: Users,
      gradient: 'from-[--clinqr-slate-dark] to-[--clinqr-slate-medium]',
    },
    {
      label: 'Last Scan',
      value: analytics?.last_scan_at
        ? new Date(analytics.last_scan_at).toLocaleDateString()
        : 'Never',
      icon: Clock,
      gradient: 'from-[--clinqr-slate-medium] to-[--clinqr-slate-light]',
    },
    {
      label: 'Unique Rate',
      value: `${uniqueRate}%`,
      icon: BarChart3,
      gradient: 'from-[--clinqr-navy] to-[--clinqr-slate-dark]',
    },
  ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-2 lg:grid-cols-4 gap-3"
    >
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          variants={itemVariants}
          className="bg-white rounded-xl p-4 border border-[--clinqr-border] hover:shadow-sm transition-shadow"
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${stat.gradient} flex items-center justify-center`}>
              <stat.icon className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            {stat.showTrend && totalScans > 0 && (
              <TrendingUp className="w-4 h-4 text-green-500" />
            )}
          </div>
          <p className="text-2xl font-bold text-[--clinqr-text-primary] mb-0.5">{stat.value}</p>
          <p className="text-xs text-[--clinqr-text-secondary]">{stat.label}</p>
        </motion.div>
      ))}
    </motion.div>
  );
}
